import { useState, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Copy,
  Check,
  Link,
  EnvelopeSimple,
  Eye,
  Globe,
  Lock,
  CircleNotch,
} from "@phosphor-icons/react";
import { Dialog, DialogHeader, DialogBody, DialogFooter } from "../ui/Dialog";
import { cn } from "../../lib/cn";
import { api } from "../../lib/api";
import { useToast } from "../ui/Toast";

const spring = { type: "spring" as const, stiffness: 500, damping: 35, mass: 0.6 };

type AccessLevel = "view" | "edit";
type LinkAccess = "restricted" | "anyone";

interface ShareDialogProps {
  open: boolean;
  onClose: () => void;
  projectId?: string;
  documentTitle?: string;
}

export function ShareDialog({ open, onClose, projectId, documentTitle }: ShareDialogProps) {
  const [email, setEmail] = useState("");
  const [accessLevel, setAccessLevel] = useState<AccessLevel>("view");
  const [linkAccess, setLinkAccess] = useState<LinkAccess>("restricted");
  const [copied, setCopied] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [generatingLink, setGeneratingLink] = useState(false);
  const toast = useToast();

  const shareUrl = `${window.location.origin}/project/${projectId}`;

  const handleCopyLink = useCallback(async () => {
    if (!projectId || generatingLink) return;

    setGeneratingLink(true);
    try {
      const data = await api.post<{ token: string }>(`/api/v1/projects/${projectId}/share-link`, {
        role: accessLevel === "edit" ? "editor" : "viewer",
      });
      const url = `${window.location.origin}/shared/${data.token}`;
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to create share link", "Please try again.");
    } finally {
      setGeneratingLink(false);
    }
  }, [projectId, accessLevel, generatingLink, toast]);

  const handleInvite = useCallback(async () => {
    if (!email.trim() || !projectId || inviting) return;
    setInviting(true);
    try {
      const role = accessLevel === "edit" ? "editor" : "viewer";
      await api.post(`/api/v1/projects/${projectId}/share`, {
        email: email.trim(),
        role,
      });
      toast.success("Invited", `${email.trim()} can now ${accessLevel} this project.`);
      setEmail("");
    } catch {
      toast.error("Invite failed", "Could not invite this person. Check the email and try again.");
    } finally {
      setInviting(false);
    }
  }, [email, projectId, accessLevel, inviting, toast]);

  return (
    <Dialog open={open} onClose={onClose} className="max-w-md">
      <DialogHeader>
        <div className="flex items-center gap-2">
          <span className="text-[15px] font-semibold text-[var(--text-primary)]">Share</span>
          {documentTitle && (
            <span className="text-[12px] text-[var(--text-muted)] truncate max-w-[200px]">
              — {documentTitle}
            </span>
          )}
        </div>
      </DialogHeader>

      <DialogBody className="space-y-5">
        {/* Invite by email */}
        <div>
          <label className="text-[11px] font-medium text-[var(--text-muted)] uppercase tracking-wider mb-2 block">
            Invite people
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <EnvelopeSimple
                size={14}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)]"
              />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleInvite()}
                placeholder="Email address"
                className={cn(
                  "w-full rounded-[var(--radius-md)] border border-[var(--border-subtle)]",
                  "bg-transparent pl-8 pr-3 py-2 text-[12px] text-[var(--text-primary)]",
                  "placeholder:text-[var(--text-muted)]",
                  "outline-none focus:border-[var(--color-lever-orange)]",
                  "transition-colors duration-100",
                )}
              />
            </div>
            <select
              value={accessLevel}
              onChange={(e) => setAccessLevel(e.target.value as AccessLevel)}
              className={cn(
                "rounded-[var(--radius-md)] border border-[var(--border-subtle)]",
                "bg-[var(--bg-surface)] px-2 py-2 text-[11px] font-medium text-[var(--text-secondary)]",
                "outline-none focus:border-[var(--color-lever-orange)]",
                "transition-colors duration-100 cursor-pointer",
              )}
            >
              <option value="view">Can view</option>
              <option value="edit">Can edit</option>
            </select>
            <motion.button
              onClick={handleInvite}
              disabled={inviting}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.96 }}
              transition={spring}
              className={cn(
                "rounded-[var(--radius-md)] px-3 py-2 text-[11px] font-semibold",
                "bg-[var(--text-primary)] text-[var(--text-inverse)]",
                "hover:opacity-90 transition-opacity duration-100 disabled:opacity-50",
              )}
            >
              {inviting ? <CircleNotch size={12} weight="bold" className="animate-spin" /> : "Invite"}
            </motion.button>
          </div>
        </div>

        {/* Link sharing */}
        <div>
          <label className="text-[11px] font-medium text-[var(--text-muted)] uppercase tracking-wider mb-2 block">
            Link sharing
          </label>
          <div className="rounded-[var(--radius-lg)] border border-[var(--border-hairline)] overflow-hidden">
            <button
              onClick={() => setLinkAccess(linkAccess === "restricted" ? "anyone" : "restricted")}
              className={cn(
                "flex items-center gap-3 w-full px-3 py-2.5",
                "hover:bg-[var(--bg-surface-hover)] transition-colors duration-100",
              )}
            >
              <div
                className={cn(
                  "flex h-8 w-8 items-center justify-center rounded-full shrink-0",
                  linkAccess === "anyone"
                    ? "bg-[var(--color-signal-green)]/10 text-[var(--color-signal-green)]"
                    : "bg-[var(--bg-surface-hover)] text-[var(--text-muted)]",
                )}
              >
                {linkAccess === "anyone" ? <Globe size={16} weight="bold" /> : <Lock size={16} weight="bold" />}
              </div>
              <div className="text-left flex-1">
                <p className="text-[12px] font-medium text-[var(--text-primary)]">
                  {linkAccess === "anyone" ? "Anyone with the link" : "Restricted"}
                </p>
                <p className="text-[10px] text-[var(--text-muted)]">
                  {linkAccess === "anyone"
                    ? "Anyone on the internet with the link can view"
                    : "Only people you invite can access"}
                </p>
              </div>
              <div className="flex items-center gap-1">
                {linkAccess === "anyone" && (
                  <span className="flex items-center gap-1 text-[10px] text-[var(--text-muted)]">
                    <Eye size={12} /> View
                  </span>
                )}
              </div>
            </button>
          </div>
        </div>

        {/* Copy link */}
        <div className="flex items-center gap-2 rounded-[var(--radius-md)] bg-[var(--bg-surface-hover)] px-3 py-2">
          <Link size={14} className="text-[var(--text-muted)] shrink-0" />
          <span className="flex-1 text-[11px] text-[var(--text-secondary)] truncate font-mono">
            {shareUrl}
          </span>
          <motion.button
            onClick={handleCopyLink}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.92 }}
            transition={spring}
            className={cn(
              "flex items-center gap-1 rounded-[var(--radius-sm)] px-2 py-1 text-[10px] font-semibold shrink-0",
              "transition-colors duration-100",
              copied
                ? "bg-[var(--color-signal-green)]/10 text-[var(--color-signal-green)]"
                : "bg-[var(--bg-surface)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] border border-[var(--border-hairline)]",
            )}
          >
            {copied ? <Check size={10} weight="bold" /> : <Copy size={10} weight="bold" />}
            {copied ? "Copied" : "Copy"}
          </motion.button>
        </div>
      </DialogBody>

      <DialogFooter>
        <motion.button
          onClick={onClose}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.97 }}
          transition={spring}
          className={cn(
            "rounded-[var(--radius-md)] px-4 py-2 text-[12px] font-medium",
            "text-[var(--text-secondary)] hover:text-[var(--text-primary)]",
            "hover:bg-[var(--bg-surface-hover)] transition-colors duration-100",
          )}
        >
          Done
        </motion.button>
      </DialogFooter>
    </Dialog>
  );
}
